import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";

import CardCalculateWithholdingTaxUX from "./CardCalculateWithholdingTaxUX";

import { EmployeeSalaryType, TAX_RATES } from "./sequence/EmployeePayment";

import formatComma from "react-lib/utils/formatComma";

type CardCalculateWithholdingTaxProps = {
  detail: EmployeeSalaryType;
  employeeFullName: string;
  onClose: () => void;
  onSave: (result: string) => void;
};

type AllowanceType = Partial<{
  child: string;
  donation: string;
  fundContribution: string;
  healthInsurance: string;
  lifeInsurance: string;
  other: string;
  personal: string;
  personCare: string;
  socialSecurityAmount: string;
  spouse: string;
}>;

const DEFAULT_ALLOWANCE: AllowanceType = {
  child: "0.00",
  donation: "0.00",
  fundContribution: "0.00",
  healthInsurance: "0.00",
  lifeInsurance: "0.00",
  other: "0.00",
  personal: "60000.00",
  personCare: "0.00",
  socialSecurityAmount: "0.00",
  spouse: "0.00",
};

const CardCalculateWithholdingTax = (props: CardCalculateWithholdingTaxProps) => {
  const [allowance, setAllowance] = useState<AllowanceType>({});

  useEffect(() => {
    // Social Security Deduction Amount × 12 (เดือน)
    const socialSecurityAmount = Number(props.detail.social_security_amount ?? 0) * 12;

    setAllowance({ ...DEFAULT_ALLOWANCE, socialSecurityAmount: socialSecurityAmount.toFixed(2) });
  }, []);

  const formattedTotalAllowance = useMemo(() => {
    const totalAllowance =
      Number(allowance.personal) +
      Number(allowance.spouse) +
      Number(allowance.personCare) +
      Number(allowance.child) +
      Number(allowance.lifeInsurance) +
      Number(allowance.healthInsurance) +
      Number(allowance.fundContribution) +
      Number(allowance.socialSecurityAmount) +
      Number(allowance.donation) +
      Number(allowance.other);

    return formatComma(totalAllowance);
  }, [allowance]);

  const calculationDetails = useMemo(() => {
    const detail = props.detail;

    // 2.1 Salary - Salary/Wages × 12 (เดือน)
    const salary = detail.amount ? Number(detail.amount) * 12 : "";
    // 2.2 Position Pay - Position Pay × 12 (เดือน)
    const positionPay = detail.position_pay ? Number(detail.position_pay) * 12 : "";
    // 2.3 License Allowance - License Allowance × 12 (เดือน)
    const licenseAllowance = detail.license_allowance ? Number(detail.license_allowance) * 12 : "";
    // 2.4 Language Pay - Language Pay × 12 (เดือน)
    const languagePay = detail.language_pay ? Number(detail.language_pay) * 12 : "";
    // 2.5 Level skill - Level skill × 12 (เดือน)
    const skillPay = detail.skill_pay ? Number(detail.skill_pay) * 12 : "";
    // แสดงยอดรวมรายได้ 2.1 - 2.5 มารวมกัน
    const totalIncome =
      Number(salary) +
      Number(positionPay) +
      Number(licenseAllowance) +
      Number(languagePay) +
      Number(skillPay);
    const formattedTotalIncome = totalIncome.toFixed(2);
    // แสดงยอดหักออกตามกฎหมาย จำนวน 100,000 บาท
    const lessLegalExpenses = "100000.00";
    // แสดงยอดเงินที่ได้หลังจากการคำนวณ - Total income - Less legal expenses
    const netIncomeAfterExpenses = Number(formattedTotalIncome) - Number(lessLegalExpenses);
    // แสดงยอดสุทธิของเงินได้รายปี โดยคำนวณจาก Net income after expenses - Less allowance = Net income
    const netIncome = netIncomeAfterExpenses - Number(formattedTotalAllowance.replaceAll(",", ""));

    return {
      languagePay: formatComma(languagePay),
      lessAllowance: formattedTotalAllowance,
      lessLegalExpenses: formatComma(lessLegalExpenses),
      licenseAllowance: formatComma(licenseAllowance),
      netIncome: formatComma(netIncome),
      netIncomeAfterExpenses: formatComma(netIncomeAfterExpenses),
      netIncomeNumeric: netIncome,
      positionPay: formatComma(positionPay),
      salary: formatComma(salary),
      skillPay: formatComma(skillPay),
      totalIncome: formatComma(formattedTotalIncome),
    };
  }, [formattedTotalAllowance, props.detail]);

  const calculationAnnualTax = useMemo(() => {
    const netIncome = calculationDetails.netIncomeNumeric;
    const taxBreakdown: Record<number, number> = {};

    let totalTax = 0;

    // คำนวณภาษีแบบขั้นบันได
    for (const rate of TAX_RATES) {
      if (netIncome > rate.min - 1) {
        // คำนวณจำนวนเงินที่ต้องเสียภาษีในช่วงนี้
        const upperBound = Math.min(netIncome, rate.max);
        const lowerBound = rate.min - 1;
        const incomeInThisBracket = upperBound - lowerBound;

        if (incomeInThisBracket > 0) {
          // เงินในช่วง × อัตราภาษี
          const taxInThisBracket = incomeInThisBracket * rate.rateNumeric;

          taxBreakdown[rate.min] = Number(taxInThisBracket.toFixed(2));
          totalTax += taxInThisBracket;
        }
      }
    }

    const taxPerMonth = totalTax / 12;

    return {
      taxPayable: formatComma(totalTax),
      taxPerMonth: formatComma(taxPerMonth),
      taxPerMonthNumeric: taxPerMonth,
      ...Object.fromEntries(
        Object.entries(taxBreakdown).map(([key, value]) => [key, formatComma(value)])
      ),
    };
  }, [calculationDetails.netIncomeNumeric]);

  const handleSave = () => {
    props.onSave(calculationAnnualTax.taxPerMonthNumeric.toFixed(2));
  };

  const handleChangeValue = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name: string = data.name;

    setAllowance({ ...allowance, [name]: data.value });
  };

  return (
    <div>
      <CardCalculateWithholdingTaxUX
        allowance={allowance}
        calculationAnnualTax={calculationAnnualTax}
        calculationDetails={calculationDetails}
        employeeFullName={props.employeeFullName}
        formattedTotalAllowance={formattedTotalAllowance}
        onChangeValue={handleChangeValue}
        onClose={props.onClose}
        onSave={handleSave}
      />
    </div>
  );
};

CardCalculateWithholdingTax.displayName = "CardCalculateWithholdingTax";

export default React.memo(CardCalculateWithholdingTax);
